import { useEffect, useCallback, useRef } from 'react'

import { useDevice } from '@/context/providers/DeviceProvider'

export const useIPadKeyboardHandler = (): void => {
  const { isIPad } = useDevice()
  const lastKnownHeight = useRef<number>(0)
  const isKeyboardOpen = useRef<boolean>(false)

  const forceViewportReset = useCallback((): void => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0

    document.documentElement.scrollLeft = 0
    document.body.scrollLeft = 0

    // Force layout reflow
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    document.body.offsetHeight
  }, [])

  const preventScroll = useCallback((e: TouchEvent): void => {
    const target = e.target as HTMLElement
    if (!target) return

    let element: HTMLElement | null = target
    while (element) {
      const style = globalThis.getComputedStyle(element)
      const overflowY = style.overflowY
      const overflowX = style.overflowX

      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        element.scrollHeight > element.clientHeight
      ) {
        return // Allow scrolling
      }
      if (
        (overflowX === 'auto' || overflowX === 'scroll') &&
        element.scrollWidth > element.clientWidth
      ) {
        return // Allow scrolling
      }

      if (
        element.classList.contains('scrollable-container') ||
        element.classList.contains('browser-article-container') ||
        element.classList.contains('MuiMenu-paper') ||
        element.classList.contains('MuiDialogContent-root')
      ) {
        return // Allow scrolling
      }

      element = element.parentElement
    }

    e.preventDefault()
  }, [])

  const handleViewportChange = useCallback((): void => {
    const viewport = window.visualViewport
    if (!viewport) return

    const currentHeight = viewport.height
    const windowHeight = window.innerHeight
    const offset = viewport.offsetTop

    const heightDifference = windowHeight - currentHeight
    const keyboardLikelyOpen = offset > 0 || heightDifference > 100

    if (keyboardLikelyOpen && !isKeyboardOpen.current) {
      isKeyboardOpen.current = true
      document.body.classList.add('keyboard-open')

      if (offset > 0) {
        document.body.style.transform = `translateY(${offset}px)`
      }
    } else if (!keyboardLikelyOpen && isKeyboardOpen.current) {
      isKeyboardOpen.current = false
      document.body.classList.remove('keyboard-open')
      document.body.style.transform = ''

      requestAnimationFrame(() => {
        forceViewportReset()

        setTimeout(() => {
          forceViewportReset()
        }, 100)

        setTimeout(() => {
          forceViewportReset()
        }, 300)
      })
    } else if (offset > 0 && isKeyboardOpen.current) {
      document.body.style.transform = `translateY(${offset}px)`
    }

    lastKnownHeight.current = currentHeight
  }, [forceViewportReset])

  const handleFocusChange = useCallback(
    (e: FocusEvent): void => {
      const target = e.target as HTMLElement
      if (!target) return

      const isInputElement =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

      if (e.type === 'focusout' && isInputElement) {
        setTimeout(() => {
          forceViewportReset()

          const viewport = window.visualViewport
          if (viewport?.offsetTop === 0 && isKeyboardOpen.current) {
            isKeyboardOpen.current = false
            document.body.classList.remove('keyboard-open')
            document.body.style.transform = ''
          }
        }, 300)
      }
    },
    [forceViewportReset],
  )

  useEffect(() => {
    if (!isIPad) return

    lastKnownHeight.current = window.visualViewport?.height || window.innerHeight

    window.visualViewport?.addEventListener('scroll', handleViewportChange)
    window.visualViewport?.addEventListener('resize', handleViewportChange)

    document.addEventListener('focusin', handleFocusChange, true)
    document.addEventListener('focusout', handleFocusChange, true)

    document.addEventListener('touchmove', preventScroll, { passive: false })

    const preventWindowScroll = (): void => {
      window.scrollTo(0, 0)
    }
    window.addEventListener('scroll', preventWindowScroll, { passive: true })

    forceViewportReset()

    handleViewportChange()

    return () => {
      window.visualViewport?.removeEventListener('scroll', handleViewportChange)
      window.visualViewport?.removeEventListener('resize', handleViewportChange)
      document.removeEventListener('focusin', handleFocusChange, true)
      document.removeEventListener('focusout', handleFocusChange, true)
      document.removeEventListener('touchmove', preventScroll)
      window.removeEventListener('scroll', preventWindowScroll)
      document.body.style.transform = ''
      document.body.classList.remove('keyboard-open')
    }
  }, [isIPad, handleViewportChange, handleFocusChange, preventScroll, forceViewportReset])
}
